import { roomHash } from '@/lib/room-links';
/**
 * Project-level Schedule section.
 *
 * Renders every schedule_entry in the project grouped by relative time,
 * with an assignee stack per row that opens an assignment picker. Both
 * the room-level Schedule tab and this view read the same data and can
 * mutate it — refresh the containing project-detail page (via reload)
 * to reflect changes.
 */

import { esc, fmtDate } from '@/lib/dom';
import { contactInitials } from '@/lib/avatar';
import { SCHEDULE_STATUS_LABEL } from '@/lib/labels';
import { openScheduleModal } from '@/lib/schedule-modal';
import { updateSchedule } from '@/lib/data/schedule';
import { assignContractor, unassignContractor } from '@/lib/data/subcontractors';
import {
  bucketize, nextWeekRangeLabel, BUCKET_ORDER, BUCKET_LABEL,
  type ScheduleBucket,
} from '@/lib/schedule-grouping';
import type {
  RoomRow, DesignerContactRow, ScheduleEntryRow, ContractorAssignmentRow,
} from '@/types/database';

// Module-scoped filter state — persists across in-page re-renders but resets
// on full page navigate. Matches the pattern used for `specStatusFilter`
// in room-detail.ts.
let roomFilter: string = 'all';           // 'all' or room id
let contactFilter: string = 'all';        // 'all' or contact id
let completeExpanded = false;

export interface RenderScheduleOptions {
  projectId: string;
  rooms: RoomRow[];
  contacts: DesignerContactRow[];
  schedule: ScheduleEntryRow[];
  assignments: ContractorAssignmentRow[];
  reload: () => void;
}

export function renderProjectScheduleSection(
  container: HTMLElement,
  opts: RenderScheduleOptions,
): void {
  const { rooms, contacts, schedule, assignments } = opts;

  // Filter down before bucketing.
  const filtered = schedule.filter((e) => {
    if (roomFilter !== 'all' && e.room_id !== roomFilter) return false;
    if (contactFilter !== 'all') {
      const assigned = assignments.some(
        (a) => a.schedule_entry_id === e.id && a.contact_id === contactFilter,
      );
      if (!assigned) return false;
    }
    return true;
  });

  const groups = bucketize(filtered);
  const nextWeekLabel = `${BUCKET_LABEL.next_week} (${nextWeekRangeLabel()})`;
  const roomName = (id: string): string => rooms.find((r) => r.id === id)?.name ?? '?';

  const totalVisible = BUCKET_ORDER.reduce((n, b) => n + groups[b].length, 0);

  container.innerHTML = `
    <section class="panel">
      <div class="section-bar">
        <div class="section-bar-left">
          <h3>Schedule</h3>
          <select data-schedule-filter="room" class="schedule-filter">
            <option value="all">All rooms</option>
            ${rooms.map((r) => `<option value="${esc(r.id)}" ${roomFilter === r.id ? 'selected' : ''}>${esc(r.name)}</option>`).join('')}
          </select>
          <select data-schedule-filter="contact" class="schedule-filter">
            <option value="all">All contractors</option>
            ${contacts.map((c) => `<option value="${esc(c.id)}" ${contactFilter === c.id ? 'selected' : ''}>${esc(c.name)}</option>`).join('')}
          </select>
        </div>
        <button class="btn-ghost" data-action="add-schedule-entry">+ Add scheduled work</button>
      </div>

      ${totalVisible === 0
        ? renderEmpty(schedule.length === 0)
        : `<div class="schedule-groups">
            ${BUCKET_ORDER
              .filter((b) => groups[b].length > 0)
              .filter((b) => b !== 'complete' || completeExpanded || groups.complete.length > 0)
              .map((b) => renderBucket(b, groups[b], rooms, contacts, assignments, roomName, nextWeekLabel))
              .join('')}
            ${!completeExpanded && groups.complete.length > 0 ? `
              <button class="schedule-complete-toggle" data-action="expand-complete">
                ✅ Show ${groups.complete.length} complete
              </button>` : ''}
          </div>`}
    </section>
  `;

  wireActions(container, opts, roomName);
}

function renderEmpty(nothingAtAll: boolean): string {
  if (nothingAtAll) {
    return `<div class="empty-inline">
      No scheduled work yet. Click "+ Add scheduled work" to plan the first task,
      or add tasks inside a specific room's Schedule tab.
    </div>`;
  }
  return `<div class="empty-inline">No scheduled work matches the current filters.</div>`;
}

function renderBucket(
  bucket: ScheduleBucket,
  entries: ScheduleEntryRow[],
  _rooms: RoomRow[],
  contacts: DesignerContactRow[],
  assignments: ContractorAssignmentRow[],
  roomName: (id: string) => string,
  nextWeekLabel: string,
): string {
  const label = bucket === 'next_week' ? nextWeekLabel : BUCKET_LABEL[bucket];
  const icon =
    bucket === 'overdue'  ? '⚠' :
    bucket === 'complete' ? '✅' :
    bucket === 'undated'  ? '·' :
    '📅';
  const headClass = bucket === 'overdue' ? 'schedule-group-head schedule-group-overdue' : 'schedule-group-head';

  return `
    <div class="schedule-group" data-bucket="${esc(bucket)}">
      <div class="${headClass}">${icon} ${esc(label)}</div>
      <div class="schedule-rows">
        ${entries.map((e) => renderRow(e, contacts, assignments, roomName)).join('')}
      </div>
    </div>
  `;
}

function renderRow(
  entry: ScheduleEntryRow,
  contacts: DesignerContactRow[],
  assignments: ContractorAssignmentRow[],
  roomName: (id: string) => string,
): string {
  const dateLabel = renderDateCell(entry.start_date, entry.end_date);
  const assigned = assignments
    .filter((a) => a.schedule_entry_id === entry.id)
    .map((a) => contacts.find((c) => c.id === a.contact_id))
    .filter((c): c is DesignerContactRow => !!c);

  return `
    <div class="schedule-row" data-entry-id="${esc(entry.id)}">
      <div class="schedule-date">${dateLabel}</div>
      <div class="schedule-task">
        <button class="schedule-task-btn" data-action="edit-entry">${esc(entry.task)}</button>
        ${entry.notes ? `<div class="muted small">${esc(entry.notes)}</div>` : ''}
      </div>
      <div class="schedule-room">
        <a href="${esc(roomHash(entry.room_id))}">${esc(roomName(entry.room_id))}</a>
      </div>
      <div class="schedule-assign">
        <button class="assignee-stack" data-action="edit-assignments" title="Assign contractors">
          ${assigned.length === 0
            ? `<span class="assignee-empty">—</span>`
            : renderAssigneeStack(assigned)}
        </button>
      </div>
      <div class="schedule-status">
        <span class="chip chip-${esc(entry.status)}" data-action="edit-status">${esc(SCHEDULE_STATUS_LABEL[entry.status])}</span>
      </div>
      <div class="schedule-open">
        <a class="btn-link" href="${esc(roomHash(entry.room_id, 'schedule'))}"
           title="Open this room’s install schedule">Open in room ↗</a>
      </div>
    </div>
  `;
}

function renderAssigneeStack(assigned: DesignerContactRow[]): string {
  const shown = assigned.slice(0, 3);
  const overflow = assigned.length - shown.length;
  return `
    ${shown.map((c) => `<span class="assignee-avatar" title="${esc(c.name)}">${esc(contactInitials(c.name))}</span>`).join('')}
    ${overflow > 0 ? `<span class="assignee-more">+${overflow}</span>` : ''}
  `;
}

function renderDateCell(start: string | null, end: string | null): string {
  if (!start) return '<span class="muted">—</span>';
  const startLabel = fmtDate(start);
  if (!end || end === start) return esc(startLabel);
  return `${esc(startLabel)} <span class="muted">→</span> ${esc(fmtDate(end))}`;
}

// ─── Action wiring ─────────────────────────────────────────────────────────

function wireActions(
  container: HTMLElement,
  opts: RenderScheduleOptions,
  roomName: (id: string) => string,
): void {
  const { projectId, rooms, contacts, schedule, assignments, reload } = opts;

  // Filter dropdowns.
  container.querySelectorAll<HTMLSelectElement>('[data-schedule-filter]').forEach((sel) => {
    sel.addEventListener('change', () => {
      const which = sel.dataset.scheduleFilter;
      if (which === 'room') roomFilter = sel.value;
      else if (which === 'contact') contactFilter = sel.value;
      renderProjectScheduleSection(container, opts);
    });
  });

  // Expand Complete.
  container.querySelector('[data-action="expand-complete"]')?.addEventListener('click', () => {
    completeExpanded = true;
    renderProjectScheduleSection(container, opts);
  });

  // + Add scheduled work.
  container.querySelector('[data-action="add-schedule-entry"]')?.addEventListener('click', () => {
    if (rooms.length === 0) {
      alert('Add a room first — every scheduled task belongs to a specific room.');
      return;
    }
    openScheduleModal({
      rooms,
      sortOrder: schedule.length,
      onDone: reload,
    });
  });

  // Per-row: edit, assign, status.
  container.querySelectorAll<HTMLElement>('.schedule-row').forEach((row) => {
    const entryId = row.dataset.entryId!;
    const entry = schedule.find((e) => e.id === entryId);
    if (!entry) return;

    row.querySelector('[data-action="edit-entry"]')?.addEventListener('click', () => {
      openScheduleModal({
        rooms, sortOrder: entry.sort_order, entry, onDone: reload,
      });
    });

    row.querySelector('[data-action="edit-assignments"]')?.addEventListener('click', () => {
      openAssignmentPicker({
        entry, contacts, assignments, projectId, rooms, onChanged: reload,
      });
    });

    row.querySelector('[data-action="edit-status"]')?.addEventListener('click', () => {
      openStatusEditor(row, entry, reload);
    });
  });

  // Silence unused-param lint (roomName used inside renderRow via the closure
  // formed at render time; wireActions is called after render).
  void roomName;
}

// ─── Assignment picker modal ───────────────────────────────────────────────

function openAssignmentPicker(opts: {
  entry: ScheduleEntryRow;
  contacts: DesignerContactRow[];
  assignments: ContractorAssignmentRow[];
  projectId: string;
  rooms: RoomRow[];
  onChanged: () => void;
}): void {
  const { entry, contacts, assignments, rooms, onChanged } = opts;
  const room = rooms.find((r) => r.id === entry.room_id);
  const mineSet = new Set(
    assignments.filter((a) => a.schedule_entry_id === entry.id).map((a) => a.contact_id),
  );
  // Keep a local map of assignmentId per contact so we can DELETE by id.
  const contactToAssignmentId = new Map<string, string>();
  assignments
    .filter((a) => a.schedule_entry_id === entry.id)
    .forEach((a) => contactToAssignmentId.set(a.contact_id, a.id));

  const overlay = document.createElement('div');
  overlay.className = 'modal';
  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    overlay.remove();
    onChanged();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  };
  document.addEventListener('keydown', onKey);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  overlay.innerHTML = `
    <form class="modal-card" novalidate style="max-width:480px">
      <h2>Assign contractors</h2>
      <div class="muted" style="font-size:13px;margin-bottom:14px">
        <strong>${esc(entry.task)}</strong>
        ${room ? ' · ' + esc(room.name) : ''}
        ${entry.start_date ? ' · ' + esc(fmtDate(entry.start_date)) : ''}
      </div>

      ${contacts.length === 0
        ? `<div class="muted" style="font-size:13px">No contacts on this project yet.</div>`
        : `<div class="assignment-list">
            ${contacts.map((c) => `
              <label class="assignment-row">
                <input type="checkbox" data-assign-contact="${esc(c.id)}" ${mineSet.has(c.id) ? 'checked' : ''} />
                <div style="flex:1">
                  <div>${esc(c.name)}${c.company ? ` <span class="muted">— ${esc(c.company)}</span>` : ''}</div>
                  ${!c.phone
                    ? `<div class="muted small">No phone — SMS reminders won't send</div>`
                    : ''}
                </div>
              </label>`).join('')}
          </div>`}

      <div class="modal-error" hidden></div>
      <div class="modal-actions">
        <span style="flex:1"></span>
        <button type="button" class="btn-primary" data-action="done">Done</button>
      </div>
    </form>
  `;

  overlay.querySelector('[data-action="done"]')!.addEventListener('click', close);
  overlay.querySelectorAll<HTMLInputElement>('[data-assign-contact]').forEach((cb) => {
    cb.addEventListener('change', async () => {
      const contactId = cb.dataset.assignContact!;
      const errBox = overlay.querySelector<HTMLDivElement>('.modal-error')!;
      errBox.hidden = true;
      try {
        if (cb.checked) {
          const row = await assignContractor(contactId, entry.id);
          contactToAssignmentId.set(contactId, row.id);
          mineSet.add(contactId);
        } else {
          const assignmentId = contactToAssignmentId.get(contactId);
          if (assignmentId) await unassignContractor(assignmentId);
          contactToAssignmentId.delete(contactId);
          mineSet.delete(contactId);
        }
      } catch (err) {
        cb.checked = !cb.checked;
        errBox.hidden = false;
        errBox.textContent = err instanceof Error ? err.message : 'Assignment failed';
      }
    });
  });

  document.body.append(overlay);
}

// ─── Inline status editor ──────────────────────────────────────────────────

function openStatusEditor(
  row: HTMLElement,
  entry: ScheduleEntryRow,
  onChanged: () => void,
): void {
  const cell = row.querySelector<HTMLElement>('.schedule-status');
  if (!cell) return;
  const chip = cell.querySelector<HTMLElement>('.chip');
  if (!chip) return;

  const select = document.createElement('select');
  select.className = 'status-select';
  (Object.keys(SCHEDULE_STATUS_LABEL) as (keyof typeof SCHEDULE_STATUS_LABEL)[]).forEach((k) => {
    const opt = document.createElement('option');
    opt.value = k;
    opt.textContent = SCHEDULE_STATUS_LABEL[k];
    if (k === entry.status) opt.selected = true;
    select.appendChild(opt);
  });
  cell.replaceChild(select, chip);
  select.focus();

  const commit = async (): Promise<void> => {
    const next = select.value as ScheduleEntryRow['status'];
    if (next === entry.status) { onChanged(); return; }
    try {
      await updateSchedule(entry.id, { status: next });
      onChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Status update failed');
      onChanged();
    }
  };
  select.addEventListener('change', () => { void commit(); });
  select.addEventListener('blur', () => { void commit(); });
}
